import React, { Dispatch, FC, useCallback, useMemo, useState, SetStateAction } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Option,
  Table,
  TableRow,
  Modal,
  ModalType,
  ButtonColor,
  ButtonSize,
} from '@code/innovance-react-ui-kit';

import { Document } from 'src/front/api/apiInstance';
import { UploadType, ModelError, DocumentPageUploadData } from 'src/front/api/generated';

import { TableWrapper, TableContainer } from 'src/front/components/FormStyles';
import preview from 'src/front/common/utils/previewUtils';
import UploadDocumentModal from './UploadDocumentModal';

const AddButton = styled(Button)`
  width: auto;
  height: 30px;
  min-width: 76px;
`;

interface UploadDocumentFormData {
  documentType: Option | null;
  files?: FileList | null;
  filePath?: string;
  explanation: string;
  isOldDocument: boolean;
  createDate: string;
  documentId: string;
  documentName: string;
}

interface Props {
  customerId?: string;
  uploadType?: UploadType;
  documentTableHeaders: string[];
  setDocumentList: Dispatch<SetStateAction<DocumentPageUploadData[]>>;
  widths: string[];
  rowCount?: number;
}

const documentType = { label: 'RD', value: 'RD' };

const UploadTable: FC<Props> = ({
  uploadType = UploadType.PTSDOCUMENT,
  customerId = '',
  setDocumentList,
  widths,
  documentTableHeaders,
  rowCount = 5,
}) => {
  const { t } = useTranslation();

  const [showUploadModal, setShowUploadModal] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [isTableVisible, setIsTableVisible] = useState<boolean>(false);
  const [isTableLoading, setIsTableLoading] = useState<boolean>(false);

  const [documentFormValues, setDocumentFormValues] = useState<UploadDocumentFormData>();
  const [tableDocuments, setTableDocuments] = useState<TableRow[]>([]);
  const [documents, setDocuments] = useState<UploadDocumentFormData[]>([]);
  const [selectedDocument, setSelectedDocument] = useState<TableRow>();
  const [hasNextPage, setHasNextPage] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<ModelError>();
  const [page, setPage] = useState<number>(1);

  const loadDocuments = useCallback(
    async (pageNumber?: number): Promise<void> => {
      setIsTableLoading(true);

      const { data } = await Document.getDocuments({
        customerId: customerId || '',
        documentType: documentType.value,
        rowCountPerPage: 5,
        pageNumber: pageNumber || 1,
        refresh: !pageNumber,
        uploadType: UploadType.PTSDOCUMENT,
      });

      if (data.result === 'OK') {
        const documentTableData: TableRow[] =
          data.documentList?.map(
            (document): TableRow => ({
              id: document.documentId,
              columns: [document.documentId || '', document.explanation],
              deletable: !document.isOldDocument,
              editable: !document.isOldDocument,
              previewable: !document.isOldDocument,
            })
          ) || [];

        setTableDocuments(documentTableData);
        setHasNextPage(data.hasNextPage);
        setPage(pageNumber || 1);
        setIsTableVisible(!!data.documentList.length);
      }

      setIsTableLoading(false);
    },
    [customerId]
  );

  const handleIdentitySubmit = async (formData: UploadDocumentFormData): Promise<void> => {
    setUploadError(undefined);

    let newDocuments;
    if (!formData.documentId) {
      const { data } = await Document.uploadDocuments(
        formData.files && formData.files[0],
        documentType.value,
        uploadType,
        formData.explanation,
        customerId
      );
      if (data.result === 'OK') {
        newDocuments = [
          {
            documentType,
            explanation: data.explanation,
            isOldDocument: data.isOldDocument,
            documentId: data.documentId,
            createDate: data.createDate,
            filePath: formData.filePath,
            documentName: data.documentName,
          },
          ...documents,
        ];
      }
    } else {
      const { data } = await Document.updateDocument({
        documentId: formData.documentId,
        explanation: formData.explanation,
        documentType: documentType.value,
        documentName: formData.documentName,
        uploadType,
      });
      if (data.result === 'OK') {
        newDocuments = documents.map((doc) =>
          doc.documentId === formData.documentId
            ? {
                documentType,
                explanation: data.explanation,
                isOldDocument: data.isOldDocument,
                documentId: data.documentId,
                createDate: data.createDate,
                filePath: formData.filePath,
                documentName: data.documentName,
              }
            : doc
        );
      }
    }

    setDocuments(newDocuments || []);
    // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
    // @ts-ignore
    setDocumentList(newDocuments || []);
    setShowUploadModal(false);
    loadDocuments();
  };

  const handleDeleteIdentity = async (): Promise<void> => {
    const { data } = await Document.deleteDocument({
      documentId: selectedDocument?.id,
      uploadType,
    });
    if (data.result === 'OK') {
      setDocuments(documents.filter((doc) => doc.documentId !== selectedDocument?.id));
      setSelectedDocument(undefined);
      setIsDeleteModalOpen(false);
      loadDocuments();
    }
  };

  const addDocument = useCallback((): void => {
    setDocumentFormValues(undefined);
    setShowUploadModal(true);
  }, []);

  const editDocument = useCallback(
    (item?: TableRow): void => {
      setDocumentFormValues(documents.find((doc) => doc.documentId === item?.id));
      setShowUploadModal(true);
    },
    [documents]
  );
  const previewDocument = useCallback(
    (item?: TableRow): void => {
      preview(documents, item);
    },
    [documents]
  );
  const deleteDocument = useCallback((item?: TableRow): void => {
    setIsDeleteModalOpen(true);
  }, []);

  const actions = useMemo(
    () => ({
      add: addDocument,
      edit: editDocument,
      delete: deleteDocument,
      preview: previewDocument,
    }),
    [addDocument, deleteDocument, editDocument, previewDocument]
  );

  const handleShowTable = (): void => {
    actions.add();
  };

  const closeUploadModal = (): void => {
    setUploadError(undefined);
    setShowUploadModal(false);
  };

  return (
    <>
      <TableWrapper>
        <TableContainer>
          {!isTableVisible ? (
            <AddButton
              color={ButtonColor.Secondary}
              onClick={handleShowTable}
              size={ButtonSize.Small}
              text={t('general_add_button')}
            />
          ) : (
            <Table
              actionConfig={actions}
              addActionText={t('general_add_button')}
              emptyTableText={t('cardEntry_documentEntry_noUploadedDocumentFound_text')}
              hasNextPage={hasNextPage}
              headers={documentTableHeaders.map((head) => t(head))}
              items={tableDocuments}
              loading={isTableLoading}
              loadPageItems={loadDocuments}
              onSelect={setSelectedDocument}
              pageNumber={page}
              selectedItem={selectedDocument}
              widths={widths}
            />
          )}
        </TableContainer>
      </TableWrapper>
      {showUploadModal ? (
        <UploadDocumentModal
          error={uploadError}
          formValues={documentFormValues}
          onClose={closeUploadModal}
          submit={handleIdentitySubmit}
        />
      ) : null}
      {isDeleteModalOpen ? (
        <Modal
          cancelText={t('general_cancel_button')}
          description={t('general_deleteRecordConfirm_text')}
          onCancel={() => setIsDeleteModalOpen((state) => !state)}
          onSubmit={handleDeleteIdentity}
          submitText={t('general_components_confirm_button')}
          title="Confirmation"
          type={ModalType.Confirmation}
        />
      ) : null}
    </>
  );
};

export default UploadTable;
